import { __awaiter } from "tslib";
import { reaction } from "mobx";
import { debounce } from "lodash";
import { isDrivingQuestionBoardType } from "../models/document/document-types";
import { kActivityDebounceDelay } from "./group-activity-broadcaster";
// Broadcasts the local user's tile focus while they are editing the class-wide
// Driving Question Board, to a class-scoped presence channel so every class member
// (not just group-mates) can see who is working where. Mirrors GroupActivityBroadcaster
// but keys on the DQB document type rather than group membership.
export class DrivingQuestionBoardActivityBroadcaster {
    constructor(db) {
        Object.defineProperty(this, "db", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "disposer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "onDisconnectHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "flush", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "flushNow", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => __awaiter(this, void 0, void 0, function* () {
                const { ui, persistentUI } = this.db.stores;
                const documentKey = persistentUI.problemWorkspace.primaryDocumentKey;
                const tileIds = ui.selectedTileIds.slice();
                // Only broadcast while the Driving Question Board is the open workspace document.
                if (!this.isDQBOpen(documentKey) || tileIds.length === 0) {
                    yield this.db.clearDQBUserActivity();
                    return;
                }
                if (!this.onDisconnectHandler) {
                    this.onDisconnectHandler = this.db.setDQBUserActivityOnDisconnect();
                }
                yield this.db.setDQBUserActivity({
                    documentKey: documentKey,
                    focus: { tileIds }
                });
            })
        });
        this.db = db;
        this.flush = debounce(this.flushNow, kActivityDebounceDelay);
    }
    start() {
        const { ui, persistentUI } = this.db.stores;
        this.disposer = reaction(() => ({
            documentKey: persistentUI.problemWorkspace.primaryDocumentKey,
            tileIds: ui.selectedTileIds.slice()
        }), () => this.flush(), { fireImmediately: true });
    }
    stop() {
        var _a;
        this.flush.cancel();
        (_a = this.disposer) === null || _a === void 0 ? void 0 : _a.call(this);
        this.disposer = null;
        const onDisconnectHandler = this.onDisconnectHandler;
        this.onDisconnectHandler = null;
        return this.db.clearDQBUserActivity()
            .catch(() => undefined)
            .finally(() => {
            void (onDisconnectHandler === null || onDisconnectHandler === void 0 ? void 0 : onDisconnectHandler.cancel());
        });
    }
    isDQBOpen(documentKey) {
        if (!documentKey)
            return false;
        const document = this.db.stores.documents.getDocument(documentKey);
        return !!document && isDrivingQuestionBoardType(document.type);
    }
}
//# sourceMappingURL=data:application/json;base64,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